import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { 
  ChefHat, Clock, CheckCircle, Bell,
  LogOut, RefreshCw, History
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KitchenDisplay = () => {
  const { storeDomain, adminPath } = useParams();
  const { user, loading: authLoading, logout, getAuthHeader, getAdminBasePath } = useAuth();
  const navigate = useNavigate();
  const basePath = adminPath ? `/${adminPath}` : (storeDomain ? `/admin/${storeDomain}` : getAdminBasePath());
  const [orders, setOrders] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('active'); // 'active' | 'history'
  const prevCountRef = useRef(-1);

  const playAlert = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      [0, 0.2].forEach(delay => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = 800;
        gain.gain.value = 0.3;
        osc.start(ctx.currentTime + delay);
        osc.stop(ctx.currentTime + delay + 0.15);
      });
    } catch {}
  };

  const loadOrders = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/kitchen/orders`, { headers: getAuthHeader() });
      const newOrders = res.data;
      
      if (newOrders.length > prevCountRef.current && prevCountRef.current >= 0) {
        playAlert();
      }
      prevCountRef.current = newOrders.length;
      setOrders(newOrders);
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        toast.error('Sin acceso a cocina');
      }
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  const loadHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/kitchen/history`, { headers: getAuthHeader() });
      setHistory(res.data);
    } catch {}
  }, [getAuthHeader]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate(adminPath ? `/${adminPath}/login` : (storeDomain ? `/admin/${storeDomain}/login` : '/admin/login'));
      return;
    }
    if ((adminPath && user.admin_path && adminPath !== user.admin_path) || (storeDomain && user.store_domain && storeDomain !== user.store_domain)) {
      logout(); navigate(adminPath ? `/${adminPath}/login` : `/admin/${storeDomain}/login`, { replace: true }); return;
    }
    loadOrders();
    const interval = setInterval(loadOrders, 5000);
    return () => clearInterval(interval);
  }, [user, authLoading]);

  const completeOrder = async (orderId, orderNumber) => {
    try {
      await axios.put(`${API}/kitchen/orders/${orderId}/complete`, {}, { headers: getAuthHeader() });
      toast.success(`Orden #${orderNumber} completada`);
      setOrders(prev => prev.filter(o => o.id !== orderId));
      if (tab === 'history') loadHistory();
    } catch (e) {
      toast.error('Error al completar');
    }
  };

  const handleLogout = () => {
    logout();
    navigate(basePath ? `${basePath}/login` : '/admin/login');
  };

  const formatTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts.endsWith('Z') ? ts : ts + 'Z');
    return d.toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Guayaquil' });
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#111827' }}>
        <ChefHat size={48} className="animate-spin text-orange-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#111827' }}>
      {/* Header */}
      <div className="px-4 py-3 flex items-center justify-between" style={{ background: '#1f2937', borderBottom: '2px solid #374151' }}>
        <div className="flex items-center gap-3">
          <ChefHat size={28} className="text-orange-400" />
          <div>
            <h1 className="text-lg font-bold text-white">Cocina</h1>
            <p className="text-xs text-gray-400">{user?.username}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Tabs */}
          <div className="flex rounded-lg overflow-hidden border border-gray-600">
            <button onClick={() => setTab('active')}
              className="px-4 py-1.5 text-xs font-bold flex items-center gap-1.5 transition-all"
              style={{ background: tab === 'active' ? '#f59e0b' : 'transparent', color: tab === 'active' ? '#000' : '#9ca3af' }}
              data-testid="kitchen-tab-active">
              <Bell size={14} /> Activos {orders.length > 0 && <span className="bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px]">{orders.length}</span>}
            </button>
            <button onClick={() => { setTab('history'); loadHistory(); }}
              className="px-4 py-1.5 text-xs font-bold flex items-center gap-1.5 transition-all"
              style={{ background: tab === 'history' ? '#f59e0b' : 'transparent', color: tab === 'history' ? '#000' : '#9ca3af' }}
              data-testid="kitchen-tab-history">
              <History size={14} /> Historial
            </button>
          </div>
          <button onClick={loadOrders} className="p-2 rounded-lg hover:bg-gray-700 text-gray-400">
            <RefreshCw size={18} />
          </button>
          <button onClick={handleLogout} className="p-2 rounded-lg hover:bg-gray-700 text-gray-400">
            <LogOut size={18} />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {tab === 'active' ? (
          orders.length === 0 ? (
            <div className="text-center py-20">
              <ChefHat size={64} className="mx-auto text-gray-600 mb-4" />
              <p className="text-gray-500 text-lg">No hay pedidos activos</p>
              <p className="text-gray-600 text-sm mt-1">Los pedidos aparecerán cuando se envíen desde las mesas</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {orders.map(order => (
                <div key={order.id} className="rounded-xl overflow-hidden"
                  style={{ background: '#1f2937', border: '2px solid #f59e0b', boxShadow: '0 0 20px rgba(245,158,11,0.1)' }}
                  data-testid={`kitchen-order-${order.id}`}>
                  {/* Order Header */}
                  <div className="px-4 py-3 flex items-center justify-between" style={{ background: '#f59e0b15' }}>
                    <div className="flex items-center gap-3">
                      <span className="text-3xl font-black text-yellow-400" data-testid={`order-number-${order.id}`}>
                        #{order.order_number}
                      </span>
                      <div>
                        <h3 className="font-bold text-white text-sm">{order.table_name}</h3>
                        <p className="text-[10px] text-gray-400">
                          <Clock size={10} className="inline mr-1" />{formatTime(order.created_at)}
                          {order.sent_by && ` · ${order.sent_by}`}
                        </p>
                      </div>
                    </div>
                    <span className="text-xs font-medium px-2 py-1 rounded-full" style={{ background: '#f59e0b30', color: '#fbbf24' }}>
                      {order.items.length} items
                    </span>
                  </div>
                  
                  {/* Items */}
                  <div className="p-3 space-y-2">
                    {order.items.map((item, idx) => (
                      <div key={idx} className="rounded-lg p-3" style={{ background: '#11182750', border: '1px solid #374151' }}>
                        <div className="flex items-center gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-sm text-white">{item.product_name}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-yellow-400 font-bold text-lg">x{item.quantity}</span>
                              {item.assigned_to && <span className="text-xs text-gray-500">({item.assigned_to})</span>}
                            </div>
                          </div>
                        </div>
                        {item.observation && (
                          <div className="mt-2 px-2.5 py-1.5 rounded-lg" style={{ background: '#f59e0b20', border: '1px solid #f59e0b40' }}>
                            <p className="text-xs font-medium text-yellow-300">{item.observation}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Complete Button */}
                  <div className="p-3 pt-0">
                    <button onClick={() => completeOrder(order.id, order.order_number)}
                      className="w-full py-3 rounded-xl text-white font-bold text-sm transition-all hover:scale-[1.02] flex items-center justify-center gap-2"
                      style={{ background: '#22c55e' }}
                      data-testid={`kitchen-complete-${order.id}`}>
                      <CheckCircle size={18} /> Completar Orden #{order.order_number}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          /* History Tab */
          history.length === 0 ? (
            <div className="text-center py-20">
              <History size={64} className="mx-auto text-gray-600 mb-4" />
              <p className="text-gray-500 text-lg">No hay historial hoy</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {history.map(order => (
                <div key={order.id} className="rounded-xl overflow-hidden" style={{ background: '#1f2937', border: '2px solid #22c55e40' }}
                  data-testid={`kitchen-history-${order.id}`}>
                  <div className="px-4 py-3 flex items-center justify-between" style={{ background: '#22c55e10' }}>
                    <div className="flex items-center gap-3">
                      <span className="text-2xl font-black text-green-400">#{order.order_number}</span>
                      <div>
                        <h3 className="font-bold text-white text-sm">{order.table_name}</h3>
                        <p className="text-[10px] text-gray-400">
                          Enviada: {formatTime(order.created_at)} · Completada: {formatTime(order.completed_at)}
                        </p>
                      </div>
                    </div>
                    <CheckCircle size={20} className="text-green-400" />
                  </div>
                  <div className="p-3 space-y-1">
                    {order.items.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm text-gray-300 px-2 py-1">
                        <span>{item.product_name}</span>
                        <span className="font-bold text-green-400">x{item.quantity}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default KitchenDisplay;
